import { useEffect, useState, useContext, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    useTheme,
    TextField,
    Chip,
    Skeleton,
    MenuItem,
    Tooltip,
    FormControl,
    Select,
} from '@mui/material';
import {
    useGetCompetitiveLandscapeQuery,
    useGetAvailableDomainsQuery,
    useGetCompetitiveLandscapeKpisQuery,
} from 'src/context/api/competitorsMonitoring/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { FixedSizeList as List } from 'react-window';
import { subDays } from 'date-fns';
import {
    setFilterCompare,
    setFilterDate,
    setFilterDateCompare
} from 'src/context/reducers/filter-settings/filterSlice';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';
import { useLocation } from 'react-router-dom';
import countriesJson from 'src/utils/countries.json';
import {CardBar} from 'src/components/competitive-intelligence/components/CardBar'; 
import { fDate } from 'src/utils/format-time';
import { mapLevelToLabel } from 'src/components/competitive-intelligence/utils';
import type { PressurePoolDatum } from 'src/components/competitive-intelligence/components/ByPressureCard';
import type { TopCompetitorStat } from 'src/components/competitive-intelligence/components/TopCompetitorsCard';
import { CompetitiveIntelligenceContext } from '../../../../../components/competitive-intelligence/components/CompetitiveIntelligenceProvider';
import Iconify from '../../../../../components/iconify';
import { CountryFilter } from '../../../../../components/competitive-intelligence/components/CountryFilter';
import { useCountryData } from '../../../../../components/competitive-intelligence/hooks/useCountryData';
import { SearchTermAnalysisHeader } from '../../../../../components/competitive-intelligence/components/SearchTermAnalysisHeader';
import ChartLandscape from '../../../../../components/competitive-intelligence/components/ChartLandscape';

export default function CompetitiveLandscape() {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const [isAbsMode, setIsAbsMode] = useState<boolean>(false);
    const [selected, setSelected] = useState<'paid' | 'blended' | 'winrate'>('blended');
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [domainFilter, setDomainFilter] = useState('');
    const dispatch = useDispatch();
    const theme: any = useTheme();
    const { countriesData } = useCountryData();
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const ciContext = useContext(CompetitiveIntelligenceContext);
    const location = useLocation();
    const ITEM_HEIGHT = 56;

    // --- KPI search-term analysis data for CardBar (same metrics as SearchTermsAnalysis) ---
    const isShoppingEnabled = workspace?.template === 'E-commerce' || workspace?.shoppingIncluded === true;
    const normalizedAdType = (Array.isArray(filterStore?.adType) && filterStore.adType.length === 0)
        ? (isShoppingEnabled ? ['TXT', 'PLA'] : ['TXT'])
        : filterStore?.adType;

    const skipStaQuery =
        !filterStore.start ||
        !filterStore.end ||
        !workspace?.id ||
        (!selectedCountry && (!Array.isArray(filterStore.countries) || filterStore.countries.length === 0));

    const {
        data: competitiveLandscapeKpis,
        isFetching: isFetchingCompetitiveLandscapeKpis,
    } = useGetCompetitiveLandscapeKpisQuery(
        {
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, 'yyyy-MM-dd') : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, 'yyyy-MM-dd') : null,
            compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
            compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,
            timestep: filterStore.timestep,
            devices: filterStore.devices,
            countries: selectedCountry ? [selectedCountry] : filterStore.countries,
            keywords: filterStore.keywords,
            adType: normalizedAdType,
            workspaceId: workspace?.id,
        },
        {
            skip: skipStaQuery,
        }
    );

    const { data, isFetching } = useGetCompetitiveLandscapeQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            startDate: filterStore?.start,
            keywords: filterStore?.keywords,
            endDate: filterStore?.end,
            isAbsMode,
            adType: filterStore?.adType,
            rateMode: selected,
            timestep: filterStore?.timestep,
            selectedCountry,
            selectedDomains,
        },
        { skip: !selectedCountry }
    );

    const buttons = [
        {
            key: 'blended',
            label: t('dashboard.global.blended_presence_rate'),
            icon: 'proicons:globe',
            tooltip: isAbsMode
                ? t('dashboard.global.definition_blended_presence_rate_abs_top')
                : t('dashboard.global.definition_blended_presence_rate_top'),
        },
        {
            key: 'paid',
            label: t('dashboard.global.paid_presence_rate'),
            icon: 'f7:money-dollar-circle',
            tooltip: isAbsMode
                ? t('dashboard.global.definition_paid_presence_rate_abs_top')
                : t('dashboard.global.definition_paid_presence_rate_top'),
        },
    ];

    useEffect(() => {
        if (ciContext?.selectedCountry != null) {
            setSelectedCountry(ciContext.selectedCountry);
        }
    }, [ciContext?.selectedCountry]);

    useEffect(() => {
        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country');
        if (!codeParam) return;
        const mappedName = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
        if (mappedName && countriesData.some((c) => c.country_name === mappedName)) {
            setSelectedCountry(mappedName);
            try {
                ciContext?.setSelectedCountry?.(mappedName);
                if (workspace?.id) localStorage.setItem(`ci.selectedCountry.${workspace.id}`, mappedName);
            } catch { }
        }
    }, [location.search, countriesData, ciContext, workspace?.id]);

    useEffect(() => {
        if (selectedCountry || !countriesData.length || !workspace?.id) return;
        try {
            const key = `ci.selectedCountry.${workspace.id}`;
            const stored = localStorage.getItem(key);
            if (stored && countriesData.some((c) => c.country_name === stored)) {
                setSelectedCountry(stored);
                ciContext?.setSelectedCountry?.(stored);
            }
        } catch { }
    }, [selectedCountry, countriesData, workspace?.id, ciContext]);

    useEffect(() => {
        if (!selectedCountry && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }
    }, [selectedCountry, countriesData]);

    const { data: availableDomainsData, isFetching: isLoadingAvailableDomains } = useGetAvailableDomainsQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            startDate: filterStore?.start,
            keywords: filterStore?.keywords,
            endDate: filterStore?.end,
            adType: filterStore?.adType,
            timestep: filterStore?.timestep,
            selectedCountry: selectedCountry,
        },
        {
            skip: !selectedCountry || !workspace?.id
        }
    );

    const allDomains = availableDomainsData?.data ?? [];
    const graphData = data?.data?.presence_rates_average_metrics_by_date ?? [];

    // Get color for a domain based on ChartLandscape palette logic
    const getDomainColor = useMemo(() => {
        const OTHER = ['#DFD09D', '#4D9FC4', '#7CD3AD', '#DDBFE9'];
        const MAIN = '#B48FFF';
        
        // Calculate top domains same way as ChartLandscape
        const metricKey = selected === 'blended' 
            ? (isAbsMode ? 'abs_top_blended_presence_rate' : 'top_blended_presence_rate')
            : selected === 'paid'
                ? (isAbsMode ? 'abs_top_paid_presence_rate' : 'top_paid_presence_rate')
                : (isAbsMode ? 'abs_top_competitor_win_rate' : 'top_competitor_win_rate');
        
        const scores = new Map<string, number>();
        for (const d of graphData) {
            scores.set(d.domain_name, (scores.get(d.domain_name) ?? 0) + (d[metricKey] ?? 0));
        }
        
        const topDomains = Array.from(scores.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([name]) => name);
        
        const colorMap = new Map<string, string>();
        topDomains.forEach((domain, i) => {
            if (domain === workspace?.url) {
                colorMap.set(domain, MAIN);
            } else {
                const offset = i > topDomains.findIndex(x => x === workspace?.url) ? i - 1 : i;
                colorMap.set(domain, OTHER[offset % OTHER.length]);
            }
        });
        
        return (domainName: string) => {
            return colorMap.get(domainName) || '#ddd';
        };
    }, [graphData, selected, isAbsMode, workspace?.url]);

    // --- CardBar metrics: pressure pools, top competitors, blended presence weekly series & top metric ---

    // Build "rows" from KPI data (similar to SearchTermsAnalysis, but we only need a subset for aggregates)
    const rows = (competitiveLandscapeKpis as any[] | undefined)?.map((item: any, index: number) => {
        const isNotScrapped = item?.status === 'not scrapped';
        return {
            id: `${item?.country_name ?? 'NA'}__${item?.search_term ?? index}`,
            searchTerm: item?.search_term ?? '',
            country: item?.country_name ?? '',
            trafficShare: item?.traffic_share ?? 0,
            levelRaw: isNotScrapped ? null : (isAbsMode ? item?.abs_level : item?.level),
            blendedPresenceRate: isNotScrapped
                ? null
                : (isAbsMode ? (item?.abs_top_blended_presence_rate ?? 0) : (item?.blended_presence_rate ?? 0)),
            top_5_competitors: Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : [],
        };
    }) ?? [];

    // Pressure pools by search term (same logic as SearchTermsAnalysis)
    const pressurePools = useMemo<PressurePoolDatum[]>(() => {
        type PressureLevel = 'high' | 'active' | 'low';
        const levelOrder: PressureLevel[] = ['high', 'active', 'low'];
        const counts: Record<PressureLevel, number> = {
            high: 0,
            active: 0,
            low: 0,
        };
        const searchTermsByLevel: Record<PressureLevel, Array<{ name: string; trafficShare: number }>> = {
            high: [],
            active: [],
            low: [],
        };

        rows.forEach((row) => {
            const key = typeof row.levelRaw === 'string' ? row.levelRaw.toLowerCase() : '';
            if (key === 'high' || key === 'active' || key === 'low') {
                counts[key as PressureLevel] += 1;
                const {searchTerm} = row;
                const trafficShare = typeof row.trafficShare === 'number' ? row.trafficShare : 0;
                if (searchTerm && typeof searchTerm === 'string') {
                    searchTermsByLevel[key as PressureLevel].push({
                        name: searchTerm,
                        trafficShare,
                    });
                }
            }
        });

        const totalTracked = levelOrder.reduce((sum, level) => sum + counts[level], 0);
        const toPercentage = (count: number) => (totalTracked > 0 ? Math.round((count / totalTracked) * 100) : 0);

        return levelOrder.map((level) => ({
            level,
            label: mapLevelToLabel(level),
            countryCount: counts[level],
            percentage: toPercentage(counts[level]),
            countries: searchTermsByLevel[level].sort((a, b) => b.trafficShare - a.trafficShare),
        }));
    }, [rows]);

    // Top competitors card data (same aggregation logic as SearchTermsAnalysis)
    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const stats = new Map<string, { total: number; count: number }>();

        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(
                workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '')
            );
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                const normalized = d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                workspaceDomains.add(normalized);
            });
        }

        (competitiveLandscapeKpis as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number' || rate <= 0) return;

                const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalizedDomain)) return;

                const current = stats.get(domain) || { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });

        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter((item) => item.paidPresenceRate > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 3);
    }, [competitiveLandscapeKpis, workspace]);

    // Weekly series (global, same as SearchTermsAnalysis)
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const firstItem = (competitiveLandscapeKpis as any[] | undefined)?.[0];
        if (Array.isArray(firstItem?.blended_presence_rate_weekly_series)) {
            return firstItem.blended_presence_rate_weekly_series;
        }
        return undefined;
    }, [competitiveLandscapeKpis]);

    // Top metric: average blended presence rate across all search terms
    const blendedPresenceTopMetric = useMemo(() => {
        const validRates = rows
            .map((row) => row.blendedPresenceRate)
            .filter((rate): rate is number => typeof rate === 'number' && rate > Math.round(0));
        if (validRates.length > 0) {
            const averageRate = validRates.reduce((sum: number, rate: number) => sum + rate, 0) / validRates.length;
            return `${Math.round(averageRate)}%`;
        }
        return undefined;
    }, [rows]);

    const aggregated = useMemo(() => Object.values(
        graphData.reduce((acc: any, row: any) => {
            if (!acc[row.domain_name]) {
                acc[row.domain_name] = { domain_name: row.domain_name, blended: 0, paid: 0, winrate: 0 };
            }
            acc[row.domain_name].blended += row.top_blended_presence_rate || 0;
            acc[row.domain_name].paid += row.top_paid_presence_rate || 0;
            acc[row.domain_name].winrate += row.top_competitor_win_rate || 0;
            return acc;
        }, {})
    ), [graphData]);

    const [hasUserSelected, setHasUserSelected] = useState(false);

    const topCompetitors = [...aggregated].filter((d: any) => d.domain_name !== workspace?.url).sort((a: any, b: any) =>
        selected === 'blended'
            ? b.blended - a.blended
            : selected === 'paid'
                ? b.paid - a.paid
                : b.winrate - a.winrate
    ).slice(0, 4);

    const presenceMetricKey = useMemo(() => {
        if (selected === 'blended') return 'blended';
        if (selected === 'paid') return 'paid';
        return 'winrate';
    }, [selected]);

    const domainPresenceScores = useMemo(() => {
        const scores = new Map<string, number>();
        aggregated.forEach((item: any) => {
            scores.set(item.domain_name, item[presenceMetricKey] ?? 0);
        });
        return scores;
    }, [aggregated, presenceMetricKey]);

    const sortedDomains = useMemo(() => {
        const mainDomainEntry = workspace?.url
            ? allDomains.find((d: any) => d.domain_name === workspace?.url)
            : null;

        const others = allDomains
            .filter(
                (d: any) =>
                    d?.domain_name &&
                    (!workspace?.url || d.domain_name !== workspace.url)
            )
            .sort(
                (a: any, b: any) =>
                    (domainPresenceScores.get(b.domain_name) ?? 0) -
                    (domainPresenceScores.get(a.domain_name) ?? 0)
            );

        return [
            ...(mainDomainEntry ? [mainDomainEntry] : []),
            ...others,
        ];
    }, [allDomains, workspace?.url, domainPresenceScores]);

    useEffect(() => {
        dispatch(setFilterDate({
            start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString()
        }));
        dispatch(setFilterCompare(false));
        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }));
    }, []);

    const Row = ({ index, style, data }: any) => {
        const {
            competitors,
            theme,
            selectedDomains,
            topCompetitors,
            setSelectedDomains,
            selected,
            getDomainColor,
        } = data;


        const d = competitors[index];
        const isTopCompetitor = !hasUserSelected && topCompetitors.some((t: any) => t.domain_name === d.domain_name);
        const isSelected = selectedDomains.includes(d.domain_name);

        const handleClick = () => {
            setHasUserSelected(true); // désactive les top auto

            setSelectedDomains((prev: string[]) => {
                if (prev.includes(d.domain_name)) {
                    // Si le domaine est déjà sélectionné, on le retire
                    return prev.filter((dom) => dom !== d.domain_name);
                }

                if (prev.length >= 4) {
                    // 🚫 Si 4 domaines déjà sélectionnés → on bloque et on avertit
                    enqueueSnackbar(`You can select up to 4 domains only.`, { variant: 'warning' });
                    return prev;
                }

                // Sinon, on ajoute le domaine
                return [...prev, d.domain_name];
            });
        };

        return (
            <div style={style} key={d.domain_name}>
                <Box
                    onClick={handleClick}
                    sx={{
                        mb: 1.5,
                        cursor: 'pointer',
                        borderRadius: '6px',
                        '&:hover': {
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.04)',
                        },
                        px: 2,
                        py: 0.25,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: .5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: isTopCompetitor || isSelected ? 1 : 0.4,
                                        bgcolor: theme.palette.mode === 'dark' ? '#0E131C' : '#E7EAEE',
                                        border: '2px solid',
                                        borderColor: getDomainColor(d.domain_name),
                                        padding: '2px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.common.white,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <img
                                            src={`https://www.google.com/s2/favicons?domain=${d.domain_name}&sz=32`}
                                            alt={d.domain_name}
                                            style={{ width: 14, height: 14, borderRadius: '50%' }}
                                        />
                                    </Box>
                                </Box>

                                {(isTopCompetitor || isSelected) && (
                                    <Iconify
                                        icon="material-symbols:check-circle"
                                        height="13px"
                                        width="13px"
                                        sx={{
                                            position: 'absolute',
                                            right: -5,
                                            top: -3,
                                            color: isSelected ? '#00c853' : '#00c853',
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ opacity: isTopCompetitor || isSelected ? 1 : 0.4 }}
                            >
                                {d.domain_name}
                            </Typography>
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selected === 'blended'
                                ? `${d.top_blended_presence_rate ?? 0}%`
                                : selected === 'paid'
                                    ? `${d.top_paid_presence_rate ?? 0}%`
                                    : `${d.top_competitor_win_rate ?? 0}%`}
                        </Typography>
                    </Box>
                </Box>
            </div>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Helmet>
                <title>Competitive Landscape</title>
            </Helmet>
            <CardBar
                blendedPresenceTitle="Blended Presence Rate"
                blendedPresenceTopMetric={blendedPresenceTopMetric}
                blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
                pressureTitle="Search Terms By Pressure"
                topCompetitorsTitle="Top Competitors"
                pressurePools={pressurePools}
                topCompetitorsData={topCompetitorsData}
                loading={isFetchingCompetitiveLandscapeKpis || isFetching}
            />

            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    borderRadius: 1,
                    border: '1px solid rgba(255,255,255,0.11)',
                    boxShadow: '0 7px 20px 0 rgba(255,255,255,0.04)',
                    background: 'transparent',
                    overflow: 'hidden'
                }}
            >
                {/* HEADER */}
                <Box sx={{ px: '25px', py: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <Box sx={{ width: 1050, maxWidth: '100%' }}>
                        <CountryFilter
                            countries={countriesData}
                            selectedCountry={selectedCountry}
                            onCountryChange={(c) => {
                                setSelectedCountry(c);
                                try { ciContext?.setSelectedCountry?.(c); } catch { }
                                try {
                                    if (workspace?.id) {
                                        const key = `ci.selectedCountry.${workspace.id}`;
                                        if (c) localStorage.setItem(key, c);
                                        else localStorage.removeItem(key);
                                    }
                                } catch { }
                            }}
                        />
                    </Box>
                </Box>

                {/* BODY */}
                <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: theme.palette.mode === 'dark' ? '#2a3037' : '#f4f6f8', mt: 0.5, minHeight: 0 }}>
                    <Box display="flex" sx={{ justifyContent: 'space-between', px: '32px', py: '10px' }}>
                        {/* Filter Buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', border: '1px solid rgba(145,158,171,0.20)', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff', height: '36px', px: '6px' }}>
                                {buttons.map((btn: any) => (
                                    <Tooltip key={btn.key} title={btn.tooltip} arrow placement="top">
                                        <Button
                                            disableElevation
                                            onClick={() => setSelected(btn.key)}
                                            disableRipple
                                            sx={(t) => ({
                                                px: '10px',
                                                py: 0,
                                                minHeight: '24px',
                                                height: '24px',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                lineHeight: '24px',
                                                color: selected === btn.key ? (theme.palette.mode === 'dark' ? t.palette.primary.contrastText : 'black') : t.palette.text.secondary,
                                                bgcolor: selected === btn.key
                                                    ? theme.palette.mode === 'dark'
                                                        ? '#37424d'
                                                        : 'rgba(145,158,171,0.12)'
                                                    : 'transparent',
                                                '&:hover': {
                                                    bgcolor: selected === btn.key
                                                        ? theme.palette.mode === 'dark'
                                                            ? '#45515c'
                                                            : 'rgba(145,158,171,0.2)'
                                                        : theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.08)'
                                                            : 'rgba(0,0,0,0.04)',
                                                },
                                            })}
                                            startIcon={<Iconify icon={btn.icon} sx={{ fontSize: 16 }} />}
                                        >
                                            <Typography sx={{ color: theme.palette.mode === 'dark' ? '#FFF' : 'black', fontSize: '13px', lineHeight: '20px' }}>
                                                {btn.label}
                                            </Typography>
                                        </Button>
                                    </Tooltip>
                                ))}
                            </Box>
                        </Box>

                        {/* Abs Top Selector */}
                        <Box display="flex" flexDirection="row">
                            <Box sx={{ display: 'flex', height: 36, p: '6px', alignItems: 'center', gap: '2px', borderRadius: '8px' }}>
                                <SearchTermAnalysisHeader isAbsMode={isAbsMode} onToggleAbsMode={() => setIsAbsMode((v: any) => !v)} />
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                    value={isAbsMode ? 'abs' : 'top'}
                                    onChange={(e) => setIsAbsMode(e.target.value === 'abs')}
                                    displayEmpty
                                    renderValue={(selected) => (
                                        <Chip
                                            label={selected === 'abs' ? 'Absolute Top' : 'Top'}
                                            sx={{
                                                bgcolor: 'rgba(145,158,171,0.20)',
                                                color: theme.palette.mode === 'dark' ? '#fff' : 'black',
                                                fontSize: '13px',
                                                height: 24,
                                                borderRadius: '6px',
                                                '& .MuiChip-label': {
                                                    px: 1.5,
                                                    lineHeight: '20px',
                                                },
                                            }}
                                        />
                                    )}
                                    sx={{
                                        borderRadius: '8px',
                                        '& .MuiSelect-select': {
                                            padding: '0 8px',
                                        },
                                        height: 36,
                                        fontSize: '14px',
                                        '.MuiOutlinedInput-notchedOutline': {
                                            border: '1px solid rgba(145,158,171,0.20)',
                                        },
                                        '.MuiSelect-icon': {
                                            color: theme.palette.mode === 'dark' ? '#fff' : 'black',
                                        },
                                    }}
                                >
                                    <MenuItem value="top">Top</MenuItem>
                                    <MenuItem value="abs">Absolute Top</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Chart + Sidebar */}

                    <Box sx={{ display: 'flex', width: '100%', height: 600, borderTop: theme.palette.mode === 'dark' ? '1px solid #40484f' : '1px solid #E9ECEE' }}>
                        {/* Chart */}
                        {(!selectedCountry || isFetching || !data) ? (
                            <Skeleton width="100%" height="calc(100vh - 280px)" variant="rectangular" animation="wave" />
                        ) : (
                            <ChartLandscape
                                data={data?.data?.presence_rates_average_metrics_by_date}
                                rateMode={selected}
                                isAbsMode={isAbsMode}
                                mainDomain={workspace?.url}
                            />
                        )}

                        {/* Sidebar domaines */}
                        {(isLoadingAvailableDomains || sortedDomains?.length === 0)
                            ? <Skeleton width={400} height="calc(100vh - 280px)" variant="rectangular" animation="wave" />
                            :

                            <Box
                                sx={{
                                    width: 400,
                                    borderLeft: theme.palette.mode === 'dark' ? '1px solid #40484f' : '1px solid #E9ECEE',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Box px={2} display="flex" justifyContent="flex-end" textAlign="left">
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#B1B9C4',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            mb: 0.5,
                                            mt: 1.5
                                        }}
                                    >
                                        {selected === "blended" && "Visibility Rate" || selected === "paid" && "Paid PR"}
                                    </Typography>
                                </Box>

                                {/* Bloc fixe : main domain + search input */}
                                {sortedDomains
                                    .filter((d) => d?.domain_name === workspace?.url)
                                    .map((d: any) => {
                                        return (
                                            <Box key={d.domain_name} sx={{ flexShrink: 0 }}>


                                                {/* Main domain + valeur */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        px: 2,
                                                        py: 0.5,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, }}>
                                                        <Box
                                                            sx={{
                                                                width: 26,
                                                                height: 26,
                                                                borderRadius: '50%',
                                                                overflow: 'hidden',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: theme.palette.mode === 'dark' ? '#0E131C' : '#E7EAEE',
                                                                border: '2px solid',
                                                                borderColor: getDomainColor(d.domain_name),
                                                                padding: '2px',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '50%',
                                                                    bgcolor: theme.palette.common.white,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <img
                                                                    src={`https://www.google.com/s2/favicons?domain=${d.domain_name}&sz=32`}
                                                                    alt={d.domain_name}
                                                                    style={{ width: 14, height: 14, borderRadius: '50%' }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {d.domain_name}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {selected === 'blended'
                                                                ? `${d.top_blended_presence_rate ?? 0}%`
                                                                : selected === 'paid'
                                                                    ? `${d.top_paid_presence_rate ?? 0}%`
                                                                    : `${d.top_competitor_win_rate ?? 0}%`}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Search input */}
                                                <Box sx={{ my: 1, borderBottom: theme.palette.mode === 'dark' ? '1px solid #40484f' : '1px solid #E9ECEE' }} />
                                                <Box sx={{ px: 1.5, py: 1.5 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Search competitors..."
                                                        value={domainFilter}
                                                        onChange={(e) => setDomainFilter(e.target.value)}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    })}

                                {/* Bloc scrollable : compétiteurs */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        pr: 0.5,
                                    }}
                                >
                                    {(() => {
                                        const competitors = sortedDomains.filter(
                                            (d) =>
                                                d?.domain_name !== workspace?.url &&
                                                d?.domain_name?.toLowerCase().includes(domainFilter.toLowerCase())
                                        );

                                        if (competitors.length === 0) {
                                            return (
                                                <Box
                                                    key="no-results"
                                                    sx={{
                                                        px: 2,
                                                        py: 2,
                                                        textAlign: 'center',
                                                        color: theme.palette.text.secondary,
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    No results
                                                </Box>
                                            );
                                        }

                                        return (
                                            <List
                                                height={440}
                                                itemCount={competitors.length}
                                                itemSize={ITEM_HEIGHT}
                                                width="100%"
                                                itemData={{
                                                    competitors,
                                                    theme,
                                                    selectedDomains,
                                                    topCompetitors,
                                                    setSelectedDomains,
                                                    selected,
                                                    getDomainColor,
                                                }}
                                            >
                                                {Row}
                                            </List>
                                        );
                                    })()}
                                </Box>

                            </Box>
                        }

                    </Box>
                </Box>
            </Paper >
        </Box >
    );
}
